import { alpha, type Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";

import { isDarkMode, primaryAccentColor } from "@/src/theme/helpers";
import { schemeSx } from "@/src/theme/schemeStyles";

const TRACK_WIDTH = 64;
const TRACK_HEIGHT = 36;
const KNOB_SIZE = 28;
const KNOB_INSET = (TRACK_HEIGHT - KNOB_SIZE) / 2;
const ICON_SIZE = 18;
const ICON_INSET = KNOB_INSET + (KNOB_SIZE - ICON_SIZE) / 2;

export type ToggleIcon = "light" | "dark";

/** Surface the toggle sits on: the header bar or a row inside the menu. */
export type ToggleSurface = "header" | "menu";

/**
 * Light mode reads the track fill from the surface underneath it:
 * Secondary/50p on the header bar, Primary/8p on the menu paper. Dark mode
 * keeps the Secondary/Selected overlay on both.
 */
const getTrackColor = (theme: Theme, surface: ToggleSurface): string => {
  if (isDarkMode(theme)) {
    return theme.palette.semantic.secondary.selected;
  }

  return surface === "menu"
    ? theme.palette.semantic.primary.selected
    : theme.palette.semantic.secondary.enabled50p;
};

export const getRootSx = schemeSx((theme: Theme): SystemStyleObject<Theme> => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 1.5,
  p: 0,
  borderRadius: 1,
  "&.Mui-focusVisible": {
    outline: `2px solid ${primaryAccentColor(theme)}`,
    outlineOffset: 2
  }
}));

export const getTrackSx = (
  theme: Theme,
  surface: ToggleSurface
): SystemStyleObject<Theme> => ({
  position: "relative",
  flexShrink: 0,
  width: TRACK_WIDTH,
  height: TRACK_HEIGHT,
  borderRadius: `${TRACK_HEIGHT / 2}px`,
  backgroundColor: getTrackColor(theme, surface),
  transition: theme.transitions.create("background-color")
});

export const getKnobSx = (
  theme: Theme,
  dark: boolean
): SystemStyleObject<Theme> => ({
  position: "absolute",
  top: KNOB_INSET,
  left: dark ? TRACK_WIDTH - KNOB_SIZE - KNOB_INSET : KNOB_INSET,
  width: KNOB_SIZE,
  height: KNOB_SIZE,
  borderRadius: "50%",
  backgroundColor: dark
    ? theme.palette.background.default
    : theme.palette.common.white,
  boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, dark ? 0.25 : 0.3)}`,
  transition: theme.transitions.create("left"),
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none"
  }
});

/**
 * Light mode moon (inactive dark icon) on the header reads Primary/Light so
 * it stays visible on the 50p grey track. The menu track is a pale primary
 * wash, so that moon keeps Text/Disabled.
 */
const getInactiveMoonColor = (theme: Theme, surface: ToggleSurface): string => {
  if (isDarkMode(theme) || surface === "menu") {
    return theme.palette.text.disabled;
  }

  return theme.palette.semantic.primary.light;
};

/**
 * Both icons sit inside the track and the knob slides underneath them. The
 * active icon sits on the knob; the inactive one stays a muted mark on the
 * track.
 */
export const getIconSx = (
  theme: Theme,
  icon: ToggleIcon,
  dark: boolean,
  surface: ToggleSurface
): SystemStyleObject<Theme> => {
  const isActive = (icon === "dark") === dark;
  const activeColor =
    icon === "dark"
      ? theme.palette.text.primary
      : theme.palette.misc.ratingActive;
  const inactiveColor =
    icon === "dark"
      ? getInactiveMoonColor(theme, surface)
      : theme.palette.text.disabled;

  return {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    ...(icon === "light" ? { left: ICON_INSET } : { right: ICON_INSET }),
    zIndex: 1,
    fontSize: ICON_SIZE,
    color: isActive ? activeColor : inactiveColor,
    transition: theme.transitions.create("color")
  };
};

export const getLabelSx = schemeSx(
  (theme: Theme): SystemStyleObject<Theme> => ({
    color: theme.palette.text.primary,
    whiteSpace: "nowrap"
  })
);
